import React, { useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import Dialog, { DialogActions, DialogContent, DialogTitle } from '../Dialog'
import type { DialogProps } from '../Dialog'
import Button from '../Button'
import { useIsMobile } from '../../hooks/useIsMobile'
import FileViewer from './FileViewer'
import type { FileViewerProps } from './FileViewer'

export interface FileViewerDialogProps extends Omit<DialogProps, 'onClose'>, FileViewerProps {
  title?: React.ReactNode
  onClose: () => void
  /**
   * Called when the user clicks Done in the footer. Omit (or pass viewOnly)
   * to render the dialog without a footer — useful for a read-only customer
   * lightbox where there's nothing to confirm.
   */
  onDone?: () => void
  /** Disable the Done button (e.g. while uploads are in progress). */
  doneDisabled?: boolean
  /** Show a spinner inside the Done button. */
  doneLoading?: boolean
  /**
   * When supplied, the footer's left button reads "Back" and calls this instead
   * of "Cancel". Used when the editor is opened from the Media Viewer so the user
   * can step back to the source picker. The corner ✕ still calls `onClose`.
   */
  onBack?: () => void
}

const useStyles = makeStyles<Theme>((theme) => ({
  paper: {
    height: 'calc(100vh - 96px)',
    maxHeight: 760,
    width: '100%',
    maxWidth: 980,
    [theme.breakpoints.down('sm')]: {
      // dvh tracks the *visible* viewport so the footer isn't hidden behind
      // mobile Safari's bottom toolbar (100%/100vh resolve to the larger frame).
      height: '100dvh',
      maxHeight: '100dvh',
      maxWidth: '100%',
      margin: 0
    }
  },
  content: {
    padding: 0,
    height: '100%',
    overflow: 'hidden'
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  }
}))

function FileViewerDialog(props: FileViewerDialogProps) {
  const {
    title = 'Edit Media',
    onClose,
    onDone,
    files,
    initialKey,
    onFileChange,
    onEditSave,
    onUpload,
    onChangeFile,
    onChangeProviderFile,
    onDelete,
    onDeleteMany,
    titleOnly,
    viewOnly,
    doneDisabled,
    doneLoading,
    onBack,
    ...dialogProps
  } = props
  const classes = useStyles({})
  const isMobile = useIsMobile()

  // When the editor is open, hide the dialog's Cancel/Done footer — the editor
  // renders its own Cancel | Save Changes footer.
  const [editing, setEditing] = useState(false)

  return (
    <Dialog
      {...dialogProps}
      maxWidth="md"
      fullWidth
      forceFullScreen={isMobile}
      onClose={onClose}
      classes={{ paper: classes.paper }}
    >
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        }
      >
        {title}
      </DialogTitle>
      <DialogContent classes={{ root: classes.content }}>
        <FileViewer
          files={files}
          initialKey={initialKey}
          onFileChange={onFileChange}
          onEditSave={onEditSave}
          onUpload={onUpload}
          onChangeFile={onChangeFile}
          onChangeProviderFile={onChangeProviderFile}
          onDelete={onDelete}
          onDeleteMany={onDeleteMany}
          titleOnly={titleOnly}
          viewOnly={viewOnly}
          onEditingChange={setEditing}
        />
      </DialogContent>
      {!editing && !viewOnly && onDone && (
        <DialogActions
          leftButton={
            <Button onClick={onBack ?? onClose} variant="textNoPadding">
              {onBack ? 'Back' : 'Cancel'}
            </Button>
          }
        >
          <Button variant="contained" onClick={onDone} disabled={doneDisabled} loading={doneLoading}>
            Done
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default FileViewerDialog
